import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	ElementRef,
	inject,
	OnInit,
	signal,
} from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { IonicModule, ModalController } from "@ionic/angular";
import { catchError, filter, finalize, switchMap } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";

import {
	CreateFoundMedicationIncidentBody,
	CreateFoundMedicationIncidentDTO,
	FoundMedicationIncidentDetail,
} from "app/shared/types";

import { SimpleToastService } from "app/shared/modules";
import { AddKnownMedicationPopupComponent } from "../../popups/add-known-medication-popup/add-known-medication-popup.component";
import { AddUnknownMedicationPopupComponent } from "../../popups/add-unknown-medication-popup/add-unknown-medication-popup.component";
import {
	DepartmentValue,
	FoundMedicationIncidentModel,
	OTHER_DEPARTMENT,
	OTHER_ROOM,
	RoomValue,
} from "../../services/found-medication-incident-model.service";
import { Option, SingleSelectListComponent } from "../../../components/single-select-list/single-select-list.component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { LocationComponent, LocationFormControls } from "../../components/location/location.component";
import * as Sentry from "@sentry/angular";
import { SharedModule } from "../../../../shared/shared.module";
import { IconButtonComponent } from "../../../components/icon-button/icon-button.component";
import { FoundMedicationIncidentSummaryComponent } from "../../components/found-medication-incident-summary/found-medication-incident-summary.component";
import { MedicationIncidentDetailsListComponent } from "../../components/medication-incident-details-list/medication-incident-details-list.component";
import { BriefingDarRemarksComponent } from "../../../components/briefing-dar-remarks-form/briefing-dar-remarks.component";

enum Step {
	DATE_LOCATION = "DATE_LOCATION",
	LIST = "LIST",
	SUMMARY = "SUMMARY",
}

@Component({
	selector: "ic-found-medication-incident-steps",
	providers: [FoundMedicationIncidentModel, IonicModule],
	styleUrls: ["./medication-incident-steps.component.scss"],
	templateUrl: "./medication-incident-steps.component.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		SharedModule,
		IconButtonComponent,
		FoundMedicationIncidentSummaryComponent,
		LocationComponent,
		SingleSelectListComponent,
		MedicationIncidentDetailsListComponent,
		BriefingDarRemarksComponent,
	],
})
export class MedicationIncidentStepsComponent implements OnInit {
	private readonly router = inject(Router);
	private readonly model = inject(FoundMedicationIncidentModel);
	private readonly changeDetectorRef = inject(ChangeDetectorRef);
	private readonly toastService = inject(SimpleToastService);
	private readonly translateService = inject(TranslateService);
	private readonly fb = inject(FormBuilder);
	private readonly destroyRef = inject(DestroyRef);

	// For pop-ups
	private readonly modalController = inject(ModalController);
	private readonly elementRef = inject(ElementRef);

	protected readonly Step = Step;
	protected activeStep = Step.DATE_LOCATION;
	protected isSaving = signal(false);

	protected readonly dateOptions: Option<string>[] = this.model.generateDateOptions();
	// @ts-expect-error TS2564
	protected selectedDate: Option<string>;
	protected dateTouched = false;
	protected readonly locationForm = this.fb.group<LocationFormControls>({
		// @ts-expect-error TS2769
		departmentId: this.fb.control<DepartmentValue>(undefined, [Validators.required]),
	});

	protected details: FoundMedicationIncidentDetail[] = [];
	protected detailsStepTouched = false;

	protected readonly summaryForm = this.fb.group({
		notes: this.fb.control<string>(""),
	});

	protected defaultBackRoute = ["home", "found-medication-incident-overview"];

	ngOnInit(): void {
		this.locationForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.updateLocationForm());
	}

	protected gotoStep(step: Step) {
		this.activeStep = step;
	}

	protected markDateLocationAsTouched() {
		this.dateTouched = true;
		this.locationForm.markAllAsTouched();
	}

	protected updateLocationForm() {
		const departmentControl = this.locationForm.get("departmentId");
		const roomControl = this.locationForm.get("roomId");
		const descriptionControl = this.locationForm.get("description");

		// @ts-expect-error TS18047
		const hasSpecificDepartmentSelected = departmentControl.value && departmentControl.value !== OTHER_DEPARTMENT;
		if (hasSpecificDepartmentSelected && !roomControl) {
			// @ts-expect-error TS2769
			this.locationForm.addControl("roomId", this.fb.control<RoomValue>(null, [Validators.required]));
		} else if (!hasSpecificDepartmentSelected && roomControl) {
			this.locationForm.removeControl("roomId");
		}

		// @ts-expect-error TS18047
		const hasOtherSelected = departmentControl.value === OTHER_DEPARTMENT || roomControl?.value === OTHER_ROOM;
		if (hasOtherSelected && !descriptionControl) {
			// @ts-expect-error TS2769
			this.locationForm.addControl("description", this.fb.control<string>("", [Validators.required]));
		} else if (!hasOtherSelected && descriptionControl) {
			this.locationForm.removeControl("description");
		}
	}

	protected deleteDetail(detail: FoundMedicationIncidentDetail) {
		this.details = this.details.filter((d) => d !== detail);
	}

	protected async showAddUnknownMedicationPopup() {
		await this.showPopup(AddUnknownMedicationPopupComponent);
	}

	protected async showAddMedicationPopup() {
		await this.showPopup(AddKnownMedicationPopupComponent);
	}

	private async showPopup(component: any) {
		const modal = await this.modalController.create({
			component,
			mode: "md",
			showBackdrop: true,
			presentingElement: this.elementRef.nativeElement,
		});

		this.model
			.checkOnlineShowDialog(this.defaultBackRoute)
			.pipe(filter(Boolean))
			.subscribe(async () => {
				await modal.present();

				const detail = await modal.onWillDismiss();
				if (detail.data) {
					this.details = [...this.details, detail.data];
					this.changeDetectorRef.markForCheck();
				}
			});
	}

	protected getCreateFoundMedicationIncidentDTO(): CreateFoundMedicationIncidentDTO {
		const location = this.locationForm?.value;
		const { departmentId, roomId, description } = location;
		const { notes } = this.summaryForm.getRawValue();

		return {
			foundIncidentDate: this.selectedDate?.value,
			location: {
				...(departmentId && departmentId !== OTHER_DEPARTMENT && { departmentId }),
				...(roomId && roomId !== OTHER_ROOM && { roomId }),
				...(description && { description }),
			},
			details: this.details,
			...(notes && { notes }),
		};
	}

	protected saveIncident() {
		this.isSaving.set(true);
		const request: CreateFoundMedicationIncidentBody = {
			incident: this.getCreateFoundMedicationIncidentDTO(),
		};

		this.model
			.checkOnlineShowDialog(this.defaultBackRoute)
			.pipe(
				filter(Boolean),
				switchMap(() => this.model.createFoundMedicationIncident(request)),
				catchError((err) => {
					this.toastService.error(this.translateService.instant("general.errors.unknown"));
					Sentry.captureException(err, (scope) => {
						scope.setTransactionName("Found medication incident POST failed");
						return scope;
					});
					throw err;
				}),
				finalize(() => {
					this.isSaving.set(false);
				}),
			)
			.subscribe(() => {
				this.navigateBack();
			});
	}

	private navigateBack() {
		this.router.navigate(this.defaultBackRoute);
	}

	protected onExit() {
		this.navigateBack();
	}
}
